import type { StateCreator } from "zustand"

import { DEFAULT_SETTINGS } from "@/consts/defaultSettings"
import { loadPersistedTheme } from "@/lib/uiThemeStorage"
import type { AppState, SettingsSlice } from "@/store/types"
import type { VisualizerSettings } from "@/types"

const RECENT_LIMIT = 10
const HISTORY_LIMIT = 50

function cloneSettings(s: VisualizerSettings): VisualizerSettings {
  return structuredClone(s)
}

export const createSettingsSlice: StateCreator<
  AppState,
  [],
  [],
  SettingsSlice
> = (set) => ({
  settings: { ...DEFAULT_SETTINGS, uiTheme: loadPersistedTheme() },
  recentColors: [],
  settingsHistory: [],
  settingsHistoryIdx: -1,

  setSetting: (key, value) =>
    set((s) => ({ settings: { ...s.settings, [key]: value } })),

  pushRecentColor: (hex) =>
    set((s) => {
      const norm = hex.toLowerCase()
      const next = [norm, ...s.recentColors.filter((c) => c !== norm)].slice(
        0,
        RECENT_LIMIT,
      )
      return { recentColors: next }
    }),

  applyTemplate: (patch) =>
    set((s) => {
      const trimmed = s.settingsHistory.slice(0, s.settingsHistoryIdx + 1)
      trimmed.push(cloneSettings(s.settings))
      while (trimmed.length > HISTORY_LIMIT) trimmed.shift()
      return {
        settings: { ...s.settings, ...patch },
        settingsHistory: trimmed,
        settingsHistoryIdx: trimmed.length - 1,
      }
    }),

  undoSettings: () =>
    set((s) => {
      if (s.settingsHistoryIdx < 0) return {}
      const i = s.settingsHistoryIdx
      const target = s.settingsHistory[i]
      const next = s.settingsHistory.slice()
      next[i] = cloneSettings(s.settings)
      return {
        settings: cloneSettings(target),
        settingsHistory: next,
        settingsHistoryIdx: i - 1,
      }
    }),

  redoSettings: () =>
    set((s) => {
      const nextIdx = s.settingsHistoryIdx + 1
      if (nextIdx >= s.settingsHistory.length) return {}
      const target = s.settingsHistory[nextIdx]
      const next = s.settingsHistory.slice()
      next[nextIdx] = cloneSettings(s.settings)
      return {
        settings: cloneSettings(target),
        settingsHistory: next,
        settingsHistoryIdx: nextIdx,
      }
    }),
})
